import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  UserIcon,
  MapPinIcon,
  ClockIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

const SecurityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    eventType: '',
    minRiskScore: '',
    startDate: '',
    endDate: ''
  });
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [filters]);

  const fetchLogs = async () => {
    try {
      const params = {};
      if (filters.eventType) params.eventType = filters.eventType;
      if (filters.minRiskScore) params.minRiskScore = filters.minRiskScore;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await api.get('/security-logs', { params });
      setLogs(response.data.logs);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching security logs:', error);
      toast.error('Failed to fetch security logs');
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/security-logs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleReview = async (logId) => {
    const notes = prompt('Enter review notes (optional):');
    
    try {
      await api.put(`/security-logs/${logId}/review`, {
        reviewNotes: notes
      });
      toast.success('Log marked as reviewed');
      fetchLogs();
    } catch (error) {
      console.error('Error reviewing log:', error);
      toast.error('Failed to review log');
    }
  };

  const getRiskColor = (score) => {
    if (score >= 80) return 'bg-red-100 text-red-800 border-red-300';
    if (score >= 60) return 'bg-orange-100 text-orange-800 border-orange-300';
    if (score >= 30) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-green-100 text-green-800 border-green-300';
  };

  const getRiskLabel = (score) => {
    if (score >= 80) return '🔴 Critical';
    if (score >= 60) return '🟠 High';
    if (score >= 30) return '🟡 Medium';
    return '🟢 Low';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <ShieldExclamationIcon className="w-8 h-8 mr-3 text-red-600" />
          Security Logs
        </h1>
        <p className="text-gray-600 mt-1">Monitor location spoofing attempts and suspicious activities</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-gray-400">
            <div className="text-sm text-gray-600">Total Logs</div>
            <div className="text-2xl font-bold text-gray-900">{stats.totalLogs}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-400">
            <div className="text-sm text-blue-600">Today</div>
            <div className="text-2xl font-bold text-blue-900">{stats.todayLogs}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-400">
            <div className="text-sm text-purple-600">Last 30 Days</div>
            <div className="text-2xl font-bold text-purple-900">{stats.last30DaysLogs}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-400">
            <div className="text-sm text-red-600">High Risk</div>
            <div className="text-2xl font-bold text-red-900">{stats.highRiskLogs}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-400">
            <div className="text-sm text-orange-600">Blocked</div>
            <div className="text-2xl font-bold text-orange-900">{stats.blockedActions}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex items-center mb-3">
          <FunnelIcon className="w-5 h-5 mr-2 text-gray-600" />
          <h3 className="font-semibold text-gray-900">Filters</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <select
            value={filters.eventType}
            onChange={(e) => setFilters({ ...filters, eventType: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Event Types</option>
            <option value="CHECK_IN">Check-In</option>
            <option value="CHECK_OUT">Check-Out</option>
            <option value="LOCATION_SPOOF">Location Spoof</option>
            <option value="DEVICE_MISMATCH">Device Mismatch</option>
            <option value="IP_MISMATCH">IP Mismatch</option>
          </select>

          <select
            value={filters.minRiskScore}
            onChange={(e) => setFilters({ ...filters, minRiskScore: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Risk Levels</option>
            <option value="30">Medium+ (30+)</option>
            <option value="60">High+ (60+)</option>
            <option value="80">Critical (80+)</option>
          </select>

          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Start Date"
          />

          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="End Date"
          />
        </div>
        <button
          onClick={() => setFilters({ eventType: '', minRiskScore: '', startDate: '', endDate: '' })}
          className="mt-3 text-sm text-blue-600 hover:text-blue-800"
        >
          Clear Filters
        </button>
      </div>

      {/* Security Logs */}
      <div className="space-y-4">
        {logs.map((log) => (
          <div 
            key={log.id} 
            className={`bg-white rounded-lg shadow-md border-l-4 ${
              log.riskScore >= 80 ? 'border-red-500' :
              log.riskScore >= 60 ? 'border-orange-500' :
              log.riskScore >= 30 ? 'border-yellow-500' : 'border-green-500'
            } overflow-hidden`}
          >
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="flex items-center text-gray-900 font-semibold">
                      <UserIcon className="w-4 h-4 mr-1" />
                      {log.user?.firstName} {log.user?.lastName}
                    </span>
                    <span className="text-sm text-gray-600">
                      ({log.user?.officialEmail})
                    </span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getRiskColor(log.riskScore)}`}>
                      {getRiskLabel(log.riskScore)} ({log.riskScore})
                    </span>
                    {log.wasBlocked && (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-600 text-white">
                        🚫 BLOCKED
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                    <span className="flex items-center">
                      <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                      {log.eventType}
                    </span>
                    <span className="flex items-center">
                      <ClockIcon className="w-4 h-4 mr-1" />
                      {moment(log.createdAt).format('MMM DD, YYYY HH:mm:ss')}
                    </span>
                    {log.ipAddress && (
                      <span className="text-xs">IP: {log.ipAddress}</span>
                    )}
                  </div>

                  {log.flags && log.flags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {log.flags.map((flag, idx) => (
                        <span 
                          key={idx}
                          className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded border border-red-200"
                        >
                          ⚠️ {flag}
                        </span>
                      ))}
                    </div>
                  )}

                  {expandedLog === log.id && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg text-xs space-y-2">
                      {log.locationData && (
                        <div>
                          <strong>Location Data:</strong>
                          <pre className="mt-1 overflow-x-auto">{JSON.stringify(log.locationData, null, 2)}</pre>
                        </div>
                      )}
                      {log.securityDetails && (
                        <div>
                          <strong>Security Details:</strong>
                          <pre className="mt-1 overflow-x-auto">{JSON.stringify(log.securityDetails, null, 2)}</pre>
                        </div>
                      )}
                      {log.deviceInfo && (
                        <div>
                          <strong>Device Info:</strong>
                          <pre className="mt-1 overflow-x-auto">{JSON.stringify(log.deviceInfo, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    {expandedLog === log.id ? 'Hide Details' : 'Show Details'}
                  </button>
                  {!log.reviewedBy && (
                    <button
                      onClick={() => handleReview(log.id)}
                      className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                    >
                      Mark Reviewed
                    </button>
                  )}
                  {log.reviewedBy && (
                    <span className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded text-center">
                      ✓ Reviewed
                    </span>
                  )}
                </div>
              </div>

              {log.reviewNotes && (
                <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                  <p className="text-sm text-blue-900">
                    <strong>Review Notes:</strong> {log.reviewNotes}
                  </p>
                  {log.reviewer && (
                    <p className="text-xs text-blue-700 mt-1">
                      Reviewed by {log.reviewer.firstName} {log.reviewer.lastName} on {moment(log.reviewedAt).format('MMM DD, YYYY')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {logs.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <ShieldExclamationIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No security logs found</p>
          <p className="text-sm text-gray-500 mt-2">This is good news! No suspicious activities detected.</p>
        </div>
      )}
    </div>
  );
};

export default SecurityLogs;

